/**
 * Calendar — appointment list grouped by date, rendered 1:1 from
 *   /Users/gudupuramesh/Documents/design ui/anti Interior io ui web/calendar.html
 *
 * Folds the old `/appointments` page into the new design folder's
 * `Calendar` (per the design-folder direction). Same Firestore
 * subscription via `useAppointments()`.
 *
 * Layout:
 *   `.projects-header` — title + "N appointments today" + Month View
 *     + "+ New Appt"
 *   `.appt-list`
 *     `.appt-group` (per date)
 *        `.appt-date` (Today / Tomorrow / Wed 24 May)
 *        `.appt-card` × N
 *          `.appt-time` (start time + end time)
 *          `.appt-details`
 *            `.appt-info` h3 + meta line (icons + location/attendees)
 *            `.appt-actions` (Reschedule / Join Call)
 *
 * Click card → opens `AppointmentSlideOver` preview (URL syncs to
 * `/calendar/{id}`).
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { canMarkAttendance } from '@shared/perms';

import { AppointmentFormSlideOver, type AppointmentFormMode } from '@/components/AppointmentFormSlideOver';
import { AppointmentSlideOver } from '@/components/AppointmentSlideOver';
import { useTopbarSearch } from '@/components/chrome/TopbarSearchContext';
import {
  appointmentBucket,
  appointmentTypeLabel,
  locationModeLabel,
  meetingPurposeLabel,
  useAppointments,
  type AppointmentItem,
  type AppointmentType,
  type LocationMode,
  type MeetingPurpose,
} from '@/lib/useAppointments';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';

// ───── Helpers ─────

function dayKey(ms: number): string {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return String(d.getTime());
}

function dayHeader(ms: number): string {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  const dateStr = d.toLocaleDateString('en-IN', {
    weekday: 'long',
    day: '2-digit',
    month: 'short',
  });
  if (diff === 0)  return `Today, ${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`;
  if (diff === 1)  return `Tomorrow, ${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`;
  if (diff === -1) return `Yesterday, ${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`;
  return dateStr;
}

function timeOf(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function endTimeOf(a: AppointmentItem): string {
  const startMs = a.scheduledAt?.toMillis?.() ?? null;
  if (!startMs) return '—';
  const mins = a.durationMins && a.durationMins > 0 ? a.durationMins : 60;
  return timeOf(startMs + mins * 60 * 1000);
}

/** Phosphor icon for the card meta line — driven by locationMode
 *  (new field) with fallback to the legacy `type` for older docs. */
function locationModeIcon(mode: LocationMode | undefined, fallback: AppointmentType): string {
  const m = mode ?? legacyModeFor(fallback);
  switch (m) {
    case 'site':   return 'ph ph-map-pin';
    case 'office': return 'ph ph-buildings';
    case 'online': return 'ph ph-video-camera';
    case 'other':  return 'ph ph-calendar-blank';
  }
}

function legacyModeFor(t: AppointmentType): LocationMode {
  switch (t) {
    case 'site_visit':     return 'site';
    case 'office_meeting': return 'office';
    case 'virtual_call':   return 'online';
    case 'other':          return 'other';
  }
}

function legacyPurposeFor(t: AppointmentType): MeetingPurpose {
  switch (t) {
    case 'site_visit':     return 'site_visit';
    case 'office_meeting': return 'initial_consultation';
    case 'virtual_call':   return 'initial_consultation';
    case 'other':          return 'other';
  }
}

/** Prefer the new field, fall back to type-derived label. */
function apptPurposeLabel(a: AppointmentItem): string {
  return meetingPurposeLabel(a.meetingPurpose ?? legacyPurposeFor(a.type));
}
function apptLocationModeLabel(a: AppointmentItem): string {
  return locationModeLabel(a.locationMode ?? legacyModeFor(a.type));
}

function isUrl(s: string | undefined): boolean {
  if (!s) return false;
  return /^https?:\/\//i.test(s);
}

function matchesSearch(a: AppointmentItem, q: string): boolean {
  if (!q.trim()) return true;
  const needle = q.trim().toLowerCase();
  const haystack = [
    a.title,
    a.location ?? '',
    a.clientName ?? '',
    a.clientPhone ?? '',
    a.notes ?? '',
    appointmentTypeLabel(a.type),
  ].join(' ').toLowerCase();
  return haystack.includes(needle);
}

// ───── Page ─────

type Tab = 'upcoming' | 'today' | 'past' | 'cancelled' | 'all';

const TABS: { key: Tab; label: string }[] = [
  { key: 'upcoming',  label: 'Upcoming' },
  { key: 'today',     label: 'Today' },
  { key: 'past',      label: 'Past' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'all',       label: 'All' },
];

export function CalendarPage() {
  const { appointments, loading, error, orgId } = useAppointments();
  const [tab, setTab] = useState<Tab>('upcoming');
  const [view, setView] = useState<'list' | 'month'>('list');
  const { role: orgRole } = useCurrentOrgRole();
  // Appointments use canMarkAttendance per firestore.rules:865
  const canWrite = canMarkAttendance(orgRole);
  // Month-cursor: which month is being shown. Defaults to current month.
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const { query: searchQuery, setPlaceholder } = useTopbarSearch();
  const navigate = useNavigate();

  // Slide-over open state is URL-driven.
  //   /calendar              → no panel
  //   /calendar/new          → create form panel
  //   /calendar/:id          → preview panel
  //   /calendar/:id/edit     → edit form panel
  // The "?date=YYYY-MM-DD" query (set by the month grid) seeds the
  // create form's date.
  const { apptId: openApptId } = useParams<{ apptId?: string }>();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const isCreatePath = pathname === '/calendar/new';
  const isEditPath = Boolean(openApptId) && pathname.endsWith('/edit');
  const isPreviewPath = Boolean(openApptId) && !isEditPath;

  const formMode: AppointmentFormMode | null = isCreatePath
    ? { kind: 'create', defaultDate: searchParams.get('date') ?? undefined }
    : isEditPath && openApptId
      ? { kind: 'edit', apptId: openApptId }
      : null;

  const [openId, setOpenId] = useState<string | null>(isPreviewPath ? openApptId! : null);
  useEffect(() => {
    setOpenId(isPreviewPath ? (openApptId ?? null) : null);
  }, [isPreviewPath, openApptId]);

  useEffect(() => {
    setPlaceholder('Search appointments by title, client, location…');
    return () => setPlaceholder('Search…');
  }, [setPlaceholder]);

  const counts = useMemo(() => {
    const c = { total: 0, upcoming: 0, today: 0, past: 0, cancelled: 0 };
    for (const a of appointments) {
      c.total += 1;
      const b = appointmentBucket(a);
      c[b] += 1;
    }
    return c;
  }, [appointments]);

  const filtered = useMemo(() => {
    return appointments.filter((a) => {
      if (tab !== 'all') {
        if (tab === 'upcoming') {
          const b = appointmentBucket(a);
          if (b !== 'upcoming' && b !== 'today') return false;
        } else if (tab !== appointmentBucket(a)) {
          return false;
        }
      }
      if (!matchesSearch(a, searchQuery)) return false;
      return true;
    });
  }, [appointments, tab, searchQuery]);

  // Group by day for the `.appt-group` layout.
  const grouped = useMemo(() => {
    const groups = new Map<string, { dateMs: number; rows: AppointmentItem[] }>();
    for (const a of filtered) {
      const ms = a.scheduledAt?.toMillis?.() ?? 0;
      if (!ms) continue;
      const k = dayKey(ms);
      if (!groups.has(k)) {
        const d = new Date(ms);
        d.setHours(0, 0, 0, 0);
        groups.set(k, { dateMs: d.getTime(), rows: [] });
      }
      groups.get(k)!.rows.push(a);
    }
    // Each group sorted by scheduledAt asc within the day.
    for (const g of groups.values()) {
      g.rows.sort(
        (a, b) =>
          (a.scheduledAt?.toMillis?.() ?? 0) - (b.scheduledAt?.toMillis?.() ?? 0),
      );
    }
    // Groups sorted by date asc.
    return Array.from(groups.values()).sort((a, b) => a.dateMs - b.dateMs);
  }, [filtered]);

  const open = (id: string) => {
    setOpenId(id);
    navigate(`/calendar/${id}`);
  };
  const closeSlideOver = () => {
    setOpenId(null);
    navigate('/calendar');
  };

  return (
    <div className="page active" id="page-calendar" style={{ display: 'block' }}>
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>Calendar</h1>
          <div className="projects-stats">
            <span>{counts.today}</span> appointment{counts.today === 1 ? '' : 's'} today
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="view-toggle">
            <button
              type="button"
              className={`view-btn${view === 'list' ? ' active' : ''}`}
              onClick={() => setView('list')}
            >
              <i className="ph ph-list" /> List
            </button>
            <button
              type="button"
              className={`view-btn${view === 'month' ? ' active' : ''}`}
              onClick={() => setView('month')}
            >
              <i className="ph ph-calendar" /> Month
            </button>
          </div>
          {canWrite ? (
            // Hidden for roles that can't write — Firestore rule blocks it anyway
            <button
              className="btn-primary"
              type="button"
              onClick={() => navigate('/calendar/new')}
              disabled={!orgId}
            >
              <i className="ph-bold ph-plus" style={{ fontSize: 14 }} /> New Appt
            </button>
          ) : null}
        </div>
      </div>

      {/* Tab chips (LIST view only) OR month nav (MONTH view). */}
      {view === 'list' ? (
        <div
          style={{
            display: 'flex',
            gap: 12,
            marginBottom: 20,
            overflowX: 'auto',
            paddingBottom: 8,
            alignItems: 'center',
          }}
          className="hide-scrollbar"
        >
          {TABS.map((t) => {
            const n = t.key === 'all' ? counts.total :
                      t.key === 'upcoming' ? counts.upcoming + counts.today :
                      counts[t.key as 'today' | 'past' | 'cancelled'];
            return (
              <button
                key={t.key}
                type="button"
                className={`filter-chip${tab === t.key ? ' active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                {n > 0 ? <span style={{ opacity: 0.7, marginLeft: 4 }}>· {n}</span> : null}
              </button>
            );
          })}
          {searchQuery ? (
            <span
              style={{
                fontSize: 12,
                color: 'var(--text-tertiary)',
                marginLeft: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              {filtered.length} result{filtered.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
          }}
        >
          <button
            type="button"
            className="btn-secondary"
            onClick={() =>
              setCursor((c) =>
                c.month === 0
                  ? { year: c.year - 1, month: 11 }
                  : { year: c.year, month: c.month - 1 },
              )
            }
            aria-label="Previous month"
          >
            <i className="ph ph-caret-left" />
          </button>
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--text-primary)',
              minWidth: 180,
              textAlign: 'center',
            }}
          >
            {new Date(cursor.year, cursor.month, 1).toLocaleDateString('en-IN', {
              month: 'long',
              year: 'numeric',
            })}
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() =>
              setCursor((c) =>
                c.month === 11
                  ? { year: c.year + 1, month: 0 }
                  : { year: c.year, month: c.month + 1 },
              )
            }
            aria-label="Next month"
          >
            <i className="ph ph-caret-right" />
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              const d = new Date();
              setCursor({ year: d.getFullYear(), month: d.getMonth() });
            }}
          >
            Today
          </button>
        </div>
      )}

      {/* Body — month view branches here when toggled. */}
      {view === 'month' ? (
        <MonthGrid
          year={cursor.year}
          month={cursor.month}
          appointments={appointments}
          onOpenAppt={(id) => open(id)}
          onClickDay={
            canWrite
              ? (ymd) => navigate(`/calendar/new?date=${ymd}`)
              : undefined
          }
        />
      ) : loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          Loading appointments…
        </div>
      ) : error ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--critical)' }}>
          Couldn't load: {error}
        </div>
      ) : appointments.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>
            <i className="ph ph-calendar-blank" />
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
            No appointments yet
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            Schedule site visits, office meetings, and client calls — they'll show up here sorted by time.
          </div>
          {canWrite && (
            <button
              className="btn-primary"
              type="button"
              onClick={() => navigate('/calendar/new')}
            >
              <i className="ph-bold ph-plus" style={{ fontSize: 14 }} /> Schedule your first
            </button>
          )}
        </div>
      ) : grouped.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 14 }}>Nothing matches this filter.</div>
          <button
            className="btn-secondary"
            type="button"
            style={{ marginTop: 12 }}
            onClick={() => setTab('all')}
          >
            Show all
          </button>
        </div>
      ) : (
        <div className="appt-list">
          {grouped.map((g) => (
            <div className="appt-group" key={g.dateMs}>
              <div className="appt-date">{dayHeader(g.dateMs)}</div>

              {g.rows.map((a) => {
                const startMs = a.scheduledAt?.toMillis?.() ?? null;
                const mode = a.locationMode ?? legacyModeFor(a.type);
                const isOnline = mode === 'online';
                const isSiteOrOffice = mode === 'site' || mode === 'office';
                const assignedTo = a.assignedToName?.trim() || a.attendees?.[0] || null;

                return (
                  <div
                    className="appt-card"
                    key={a.id}
                    onClick={() => open(a.id)}
                    style={
                      isOnline
                        ? { borderLeft: '3px solid var(--brand)' }
                        : undefined
                    }
                  >
                    <div className="appt-time">
                      <span className="appt-time-start">{timeOf(startMs)}</span>
                      <span className="appt-time-end">{endTimeOf(a)}</span>
                    </div>
                    <div className="appt-details">
                      <div className="appt-info">
                        {/* Eyebrow — uppercase meeting purpose. */}
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: 'var(--text-tertiary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: 4,
                          }}
                        >
                          {apptPurposeLabel(a)}
                        </div>
                        <h3>{a.title}</h3>
                        <p>
                          <i className={locationModeIcon(a.locationMode, a.type)} />{' '}
                          {apptLocationModeLabel(a)}
                          {assignedTo ? (
                            <>
                              {' '}&nbsp;&middot;&nbsp;{' '}
                              <i className="ph ph-users" /> Assigned: {assignedTo}
                            </>
                          ) : null}
                        </p>
                      </div>
                      <div className="appt-actions">
                        {isOnline ? (
                          isUrl(a.location) ? (
                            <a
                              className="btn-primary"
                              href={a.location}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ textDecoration: 'none' }}
                            >
                              <i className="ph ph-video-camera" /> Join Call
                            </a>
                          ) : (
                            <button
                              className="btn-primary"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                open(a.id);
                              }}
                            >
                              <i className="ph ph-video-camera" /> Join Call
                            </button>
                          )
                        ) : isSiteOrOffice ? (
                          <button
                            className="btn-secondary"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/calendar/${a.id}/edit`);
                            }}
                          >
                            Reschedule
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Right-side preview slide-over */}
      <AppointmentSlideOver apptId={openId} onClose={closeSlideOver} />

      {/* Create / Edit form slide-over (single component, mode driven
          by the URL). */}
      <AppointmentFormSlideOver
        open={formMode !== null}
        onClose={() => navigate('/calendar')}
        orgId={orgId}
        mode={formMode}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MonthGrid — 7×6 calendar grid for the month view.
//
// Verbatim markup from `calendar.html` (`.month-grid` → `.appt-pill`).
// Cells that aren't in the current month get the `.disabled` class.
// Today gets `.today`. Each cell shows up to 3 appointment pills with
// a colour modifier per appointment type (site / online / office).
// Clicking a pill opens the preview; clicking a day opens the create
// form pre-filled with that date.
// ═══════════════════════════════════════════════════════════════

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function MonthGrid({
  year,
  month,
  appointments,
  onOpenAppt,
  onClickDay,
}: {
  year: number;
  month: number; // 0-11
  appointments: AppointmentItem[];
  onOpenAppt: (id: string) => void;
  /** YYYY-MM-DD passed back when a day cell is clicked. When omitted
   *  the cell is no longer click-actionable (read-only viewer). */
  onClickDay?: (ymd: string) => void;
}) {
  // Build a 6-week (42-day) grid starting from the Sunday on/before
  // the 1st of the month. Matches the folder layout 1:1.
  const cells = useMemo(() => {
    const firstOfMonth = new Date(year, month, 1);
    const firstWeekday = firstOfMonth.getDay(); // 0 = Sun
    const startMs = new Date(year, month, 1 - firstWeekday).getTime();
    const out: { date: Date; inMonth: boolean; isToday: boolean }[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 42; i++) {
      const d = new Date(startMs + i * 86400000);
      out.push({
        date: d,
        inMonth: d.getMonth() === month,
        isToday: d.getTime() === today.getTime(),
      });
    }
    return out;
  }, [year, month]);

  // Bucket appointments by YYYY-MM-DD key for O(1) lookup per cell.
  const byDay = useMemo(() => {
    const m = new Map<string, AppointmentItem[]>();
    for (const a of appointments) {
      const ms = a.scheduledAt?.toMillis?.();
      if (!ms) continue;
      const d = new Date(ms);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const arr = m.get(k) ?? [];
      arr.push(a);
      m.set(k, arr);
    }
    // Sort each day's appointments by time asc.
    for (const arr of m.values()) {
      arr.sort(
        (a, b) =>
          (a.scheduledAt?.toMillis?.() ?? 0) - (b.scheduledAt?.toMillis?.() ?? 0),
      );
    }
    return m;
  }, [appointments]);

  return (
    <div className="month-grid">
      {DAY_HEADERS.map((d) => (
        <div className="month-header" key={d}>{d}</div>
      ))}
      {cells.map((c, i) => {
        const k = `${c.date.getFullYear()}-${String(c.date.getMonth() + 1).padStart(2, '0')}-${String(c.date.getDate()).padStart(2, '0')}`;
        const dayAppts = byDay.get(k) ?? [];
        const showAppts = dayAppts.slice(0, 3);
        const overflow = dayAppts.length - showAppts.length;
        return (
          <div
            key={i}
            className={`month-day${!c.inMonth ? ' disabled' : ''}${c.isToday ? ' today' : ''}`}
            onClick={
              onClickDay
                ? (e) => {
                    if ((e.target as HTMLElement).closest('.appt-pill')) return;
                    onClickDay(k);
                  }
                : undefined
            }
            style={{ cursor: c.inMonth && onClickDay ? 'pointer' : 'default' }}
          >
            <span className="date-num">{c.date.getDate()}</span>
            {showAppts.map((a) => (
              <div
                key={a.id}
                className={`appt-pill ${pillClassFor(a.type)}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenAppt(a.id);
                }}
                title={a.title}
              >
                {pillLabel(a)}
              </div>
            ))}
            {overflow > 0 ? (
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--text-tertiary)',
                  paddingLeft: 6,
                  marginTop: 2,
                }}
              >
                +{overflow} more
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function pillClassFor(t: AppointmentType): 'p-site' | 'p-online' | 'p-office' {
  switch (t) {
    case 'site_visit':     return 'p-site';
    case 'virtual_call':   return 'p-online';
    case 'office_meeting': return 'p-office';
    case 'other':          return 'p-office';
  }
}

function pillLabel(a: AppointmentItem): string {
  const ms = a.scheduledAt?.toMillis?.();
  const time = ms
    ? new Date(ms).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
    : '';
  // The folder shows "10:30 AM Aakash Villa" — time + short title.
  return `${time} ${a.title}`;
}
